import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import type {
  AppDetail,
  AppSummary,
  CatalogResponse,
  CategoriesResponse,
  CategoryId,
  Locale,
} from '@appwin/shared';
import { API_BASE_URL } from './config.js';

export class ApiError extends Error {
  constructor(
    readonly status: number,
    readonly code: string,
    message: string,
  ) {
    super(message);
    this.name = 'ApiError';
  }
}

async function request<T>(path: string, init?: RequestInit): Promise<T> {
  const response = await fetch(`${API_BASE_URL}/v1${path}`, {
    ...init,
    headers: { accept: 'application/json', ...init?.headers },
  });
  if (!response.ok) {
    let code = 'internal';
    let message = `request failed (${response.status})`;
    try {
      const body = (await response.json()) as { error?: { code?: string; message?: string } };
      code = body.error?.code ?? code;
      message = body.error?.message ?? message;
    } catch {
      // keep the status-based message
    }
    throw new ApiError(response.status, code, message);
  }
  return (await response.json()) as T;
}

export interface CatalogQuery {
  q?: string;
  category?: CategoryId;
  cursor?: string;
  limit?: number;
}

export function useCatalog(locale: Locale, query: CatalogQuery, skipInitial = false): {
  data: CatalogResponse | null;
  loading: boolean;
  error: ApiError | null;
} {
  const [data, setData] = useState<CatalogResponse | null>(null);
  const [loading, setLoading] = useState(!skipInitial);
  const [error, setError] = useState<ApiError | null>(null);
  const skipped = useRef(skipInitial);
  const key = useMemo(
    () => JSON.stringify({ q: query.q ?? '', category: query.category ?? '', cursor: query.cursor ?? '', limit: query.limit ?? 24 }),
    [query.q, query.category, query.cursor, query.limit],
  );

  useEffect(() => {
    const controller = new AbortController();
    const params = new URLSearchParams({ locale, platform: 'windows' });
    if (query.q) params.set('q', query.q);
    if (query.category) params.set('category', query.category);
    if (query.cursor) params.set('cursor', query.cursor);
    params.set('limit', String(query.limit ?? 24));

    // The prerendered HTML already shows this exact list; fetch on the first
    // user-driven change only (search, category, cursor).
    if (skipped.current) {
      skipped.current = false;
      return () => controller.abort();
    }

    setLoading(true);
    setError(null);
    request<CatalogResponse>(`/catalog?${params.toString()}`, { signal: controller.signal })
      .then(setData)
      .catch((cause: unknown) => {
        if (cause instanceof DOMException && cause.name === 'AbortError') return;
        setError(cause instanceof ApiError ? cause : new ApiError(0, 'network', 'network error'));
      })
      .finally(() => setLoading(false));

    return () => controller.abort();
    // `key` covers every field of `query` that affects the request; the object
    // itself is rebuilt on each render and must not be a dependency.
  }, [key, locale]);

  return { data, loading, error };
}

export function useAppDetail(locale: Locale, appId: string | null): {
  data: AppDetail | null;
  loading: boolean;
  error: ApiError | null;
} {
  const [data, setData] = useState<AppDetail | null>(null);
  const [loading, setLoading] = useState(Boolean(appId));
  const [error, setError] = useState<ApiError | null>(null);

  useEffect(() => {
    if (!appId) {
      setData(null);
      setLoading(false);
      return;
    }
    const controller = new AbortController();
    setLoading(true);
    setError(null);
    const [developer, app] = appId.split('/');
    request<AppDetail>(`/apps/${developer}/${app}?locale=${locale}`, { signal: controller.signal })
      .then(setData)
      .catch((cause: unknown) => {
        if (cause instanceof DOMException && cause.name === 'AbortError') return;
        setError(cause instanceof ApiError ? cause : new ApiError(0, 'network', 'network error'));
      })
      .finally(() => setLoading(false));
    return () => controller.abort();
  }, [appId, locale]);

  return { data, loading, error };
}

export function useCategories(locale: Locale): CategoriesResponse | null {
  const [data, setData] = useState<CategoriesResponse | null>(null);
  useEffect(() => {
    const controller = new AbortController();
    request<CategoriesResponse>(`/categories?locale=${locale}`, { signal: controller.signal })
      .then(setData)
      .catch(() => setData(null));
    return () => controller.abort();
  }, [locale]);
  return data;
}

/** Debounces the search box without dropping keystrokes on re-render. */
export function useDebounced<T>(value: T, delayMs = 250): T {
  const [debounced, setDebounced] = useState(value);
  useEffect(() => {
    const timer = setTimeout(() => setDebounced(value), delayMs);
    return () => clearTimeout(timer);
  }, [value, delayMs]);
  return debounced;
}

export function useIntersection(onVisible: () => void): (node: Element | null) => void {
  const callback = useRef(onVisible);
  callback.current = onVisible;
  const observer = useRef<IntersectionObserver | null>(null);

  return useCallback((node: Element | null) => {
    observer.current?.disconnect();
    if (!node) return;
    observer.current = new IntersectionObserver((entries) => {
      if (entries.some((entry) => entry.isIntersecting)) callback.current();
    });
    observer.current.observe(node);
  }, []);
}

export type { AppSummary };
